import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { TocItem } from "@/components/article-toc";

import { LocationTakeaways } from "@/components/location-takeaways";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

interface Tab { label: string; heading: string; body: string }

interface Section {
  kind: "opening" | "body" | "faq" | "tabs";
  heading?: string;
  body: string;
  faqs?: { q: string; a: string }[];
  tabs?: Tab[];
}

/** Match the three programmatic sections we collapse into a tabbed block. */
const TAB_MATCHERS: { label: string; test: RegExp }[] = [
  { label: "What", test: /^what makes our .+ hr outsourcing service/i },
  { label: "How", test: /^how does hr outsourcing work/i },
  { label: "Why", test: /^why use hrsc/i },
];

function tabLabelFor(heading: string): string | null {
  for (const m of TAB_MATCHERS) if (m.test.test(heading)) return m.label;
  return null;
}

/** Ordered list of phrase → service-page href. First match per section wins
 *  for each slug so we internally link relevant terms without over-linking. */
const SERVICE_LINKS: { pattern: RegExp; href: string }[] = [
  { pattern: /\bstaff handbooks?\b|\bemployee handbooks?\b/i, href: "/services/staff-handbooks" },
  { pattern: /\bTUPE(?: transfers?)?\b/, href: "/services/tupe-advice" },
  { pattern: /\bredundancy(?: (?:procedures?|processes?|law|selection|costs?))?\b/i, href: "/services/redundancy-support" },
  { pattern: /\btribunal(?: representation| indemnity| defence| protection| awards?| risks?)?\b/i, href: "/services/employment-tribunal-support" },
  { pattern: /\bcontracts?(?: of employment)?\b/i, href: "/services/employment-law-advice" },
  { pattern: /\brecruitment\b/i, href: "/services/recruitment-and-selection" },
  { pattern: /\bhealth and safety\b/i, href: "/services/health-and-safety" },
  { pattern: /\bHR Health Check\b/i, href: "/services/hr-health-check" },
  { pattern: /\bHR outsourcing\b|\boutsourced HR\b/i, href: "/services/hr-outsourcing" },
  { pattern: /\bemployment law\b|\bwork law\b/i, href: "/services/employment-law-advice" },
  { pattern: /\bSMEs?\b/, href: "/services/hr-for-smes" },
  { pattern: /\bstartups?\b/i, href: "/services/hr-for-startups" },
];

/** Insert markdown links for known service phrases. First hit per slug per
 *  call. Skips content already inside a markdown link or a heading. */
function linkifyServices(md: string): string {
  const used = new Set<string>();
  return md
    .split("\n")
    .map((line) => {
      if (/^\s{0,3}#/.test(line)) return line; // don't touch headings
      let out = line;
      for (const { pattern, href } of SERVICE_LINKS) {
        if (used.has(href)) continue;
        // Skip if the line already contains a markdown link (keeps it simple)
        const m = out.match(pattern);
        if (!m) continue;
        const idx = m.index ?? -1;
        if (idx < 0) continue;
        // Avoid replacing inside an existing [text](url)
        const before = out.slice(0, idx);
        const openBrackets = (before.match(/\[/g) || []).length;
        const closeBrackets = (before.match(/\]/g) || []).length;
        if (openBrackets > closeBrackets) continue;
        out = out.slice(0, idx) + `[${m[0]}](${href})` + out.slice(idx + m[0].length);
        used.add(href);
      }
      return out;
    })
    .join("\n");
}


/** Split rendered markdown into opening + H2 sections; FAQ section (H2 with
 *  "FAQ" in the title) is extracted into a question/answer accordion. */
function parseSections(md: string): Section[] {
  const lines = md.split("\n");
  const sections: Section[] = [];
  let current: { heading?: string; buf: string[] } = { buf: [] };

  const flush = () => {
    const body = current.buf.join("\n").trim();
    if (!current.heading && !body) return;
    if (current.heading && /\bfaqs?\b/i.test(current.heading)) {
      const faqs: { q: string; a: string }[] = [];
      const inner = body.split("\n");
      let q: string | null = null;
      let a: string[] = [];
      for (const l of inner) {
        const m = l.match(/^###\s+(.+)$/);
        if (m) {
          if (q) faqs.push({ q, a: a.join("\n").trim() });
          q = m[1].trim();
          a = [];
        } else if (q !== null) {
          a.push(l);
        }
      }
      if (q) faqs.push({ q, a: a.join("\n").trim() });
      sections.push({ kind: "faq", heading: current.heading, body: "", faqs });
    } else if (!current.heading) {
      sections.push({ kind: "opening", body });
    } else {
      sections.push({ kind: "body", heading: current.heading, body });
    }
  };

  for (const line of lines) {
    const m = line.match(/^##\s+(.+)$/);
    if (m) {
      flush();
      current = { heading: m[1].trim(), buf: [] };
    } else {
      current.buf.push(line);
    }
  }
  flush();
  return sections;
}

interface Props {
  body: string;
  location: string;
  /** Rendered immediately before the last body section. */
  accreditations?: ReactNode;
  /** Rendered between the body sections and the FAQ accordion. */
  mapSlot?: ReactNode;
  /** Rendered as a right-column aside inside the opening (intro) section. */
  openingAside?: ReactNode;
}

function InlineCTA({ location }: { location: string }) {
  return (
    <section className="bg-brand px-6 py-10 text-brand-foreground lg:px-8">
      <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
        <p className="font-serif text-xl leading-snug sm:text-2xl">
          Talk to a senior HR expert about your {location} team today.
        </p>
        <Link
          to="/contact-hr-experts"
          className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-3 text-sm font-medium text-accent-foreground transition hover:brightness-110"
        >
          Get a free consultation
          <ArrowRight className="size-4" />
        </Link>
      </div>
    </section>
  );
}

export function LocationArticle({ body, location, accreditations, mapSlot, openingAside }: Props) {
  const rawSections = parseSections(body);

  // Collapse consecutive What/How/Why body sections into a single tabs block.
  const sections: Section[] = [];
  const tabsBuf: Tab[] = [];
  const flushTabs = () => {
    if (tabsBuf.length) {
      sections.push({ kind: "tabs", body: "", tabs: [...tabsBuf] });
      tabsBuf.length = 0;
    }
  };
  for (const s of rawSections) {
    const label = s.kind === "body" && s.heading ? tabLabelFor(s.heading) : null;
    if (label) {
      tabsBuf.push({ label, heading: s.heading!, body: s.body });
    } else {
      flushTabs();
      sections.push(s);
    }
  }
  flushTabs();

  const totalBody = sections.filter((s) => s.kind === "body" || s.kind === "tabs").length;
  let bodyIndex = 0;

  // Build the table-of-contents list ahead of render so IDs stay stable
  const tocItems: TocItem[] = sections
    .filter((s) => s.kind !== "opening")
    .map((s, idx) => {
      if (s.kind === "tabs") {
        return { id: `tabs-heading-${idx}`, label: `HR outsourcing in ${location}` };
      }
      const heading = s.heading ?? "";
      return { id: `section-${slugify(heading)}-${idx}`, label: heading };
    });
  let tocCursor = 0;

  return (
    <article>
      {/* Stats strip */}
      <div className="border-y border-border bg-brand text-brand-foreground">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-brand-foreground/10 sm:grid-cols-4">
          {[
            { k: "From £200", v: "Fixed monthly fee" },
            { k: "24/7", v: "Advisory helpline" },
            { k: "CIPD", v: "Qualified advisers" },
            { k: "98%", v: "Tribunal success rate" },
          ].map((s) => (
            <div key={s.k} className="bg-brand px-6 py-8 text-center">
              <p className="font-serif text-3xl leading-none">{s.k}</p>
              <p className="mt-2 text-[11px] uppercase tracking-widest text-brand-foreground/70">{s.v}</p>
            </div>
          ))}
        </div>
      </div>

      {sections.map((section, i) => {
        if (section.kind === "opening") {
          return (
            <div key={i}>
              <section className="bg-background px-6 py-20 lg:px-8">
                <div className={openingAside ? "mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12" : "mx-auto max-w-3xl"}>
                  <div className={openingAside ? "lg:col-span-8" : ""}>
                    <div className="article-markdown article-dropcap">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.body}</ReactMarkdown>
                    </div>
                    <LocationTakeaways location={location} />
                  </div>
                  {openingAside && (
                    <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
                      {openingAside}
                    </aside>
                  )}
                </div>
              </section>
              <InlineCTA location={location} />
            </div>
          );
        }
        if (section.kind === "tabs") {
          bodyIndex += 1;
          const alt = bodyIndex % 2 === 0;
          const showCTAAfter = bodyIndex === 1 || bodyIndex === 3;
          const showAccreditationsBefore = bodyIndex === totalBody && accreditations;
          const tabs = section.tabs ?? [];
          const headingId = tocItems[tocCursor++]?.id ?? `tabs-heading-${i}`;
          return (
            <div key={i}>
              {showAccreditationsBefore ? accreditations : null}
              <section
                aria-labelledby={headingId}
                className={`px-6 py-20 lg:px-8 ${alt ? "bg-muted/40" : "bg-background"}`}
              >
                <div className="mx-auto max-w-3xl">
                  <p className="text-xs font-semibold uppercase tracking-widest text-accent">
                    What / How / Why
                  </p>
                  <h2
                    id={headingId}
                    className="mt-3 font-serif text-3xl leading-tight text-brand sm:text-4xl scroll-mt-24"
                  >
                    HR outsourcing in {location}, explained
                  </h2>
                  <Tabs defaultValue={tabs[0]?.label ?? "What"} className="mt-8">
                    <TabsList
                      aria-label={`HR outsourcing in ${location}, What, How and Why`}
                      className="grid w-full grid-cols-3"
                    >
                      {tabs.map((t) => (
                        <TabsTrigger
                          key={t.label}
                          value={t.label}
                          aria-label={t.heading}
                        >
                          <span aria-hidden="true">{t.label}</span>
                          <span className="sr-only">{t.heading}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {tabs.map((t) => (
                      <TabsContent
                        key={t.label}
                        value={t.label}
                        tabIndex={0}
                        className="mt-6"
                      >
                        <h3 className="font-serif text-2xl leading-tight text-brand">
                          {t.heading}
                        </h3>
                        <div className="mt-4 article-markdown">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {linkifyServices(t.body)}
                          </ReactMarkdown>
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </section>
              {showCTAAfter ? <InlineCTA location={location} /> : null}
            </div>
          );
        }
        if (section.kind === "body") {
          bodyIndex += 1;
          const alt = bodyIndex % 2 === 0;
          const showCTAAfter = bodyIndex === 1 || bodyIndex === 3;
          const showAccreditationsBefore = bodyIndex === totalBody && accreditations;
          const headingId = tocItems[tocCursor++]?.id ?? `section-${i}`;
          return (
            <div key={i}>
              {showAccreditationsBefore ? accreditations : null}
              <section className={`px-6 py-20 lg:px-8 ${alt ? "bg-muted/40" : "bg-background"}`}>
                <div className="mx-auto max-w-3xl">
                  <h2
                    id={headingId}
                    className="font-serif text-3xl leading-tight text-brand sm:text-4xl scroll-mt-24"
                  >
                    {section.heading}
                  </h2>
                  <div className="mt-6 article-markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{linkifyServices(section.body)}</ReactMarkdown>
                  </div>
                </div>
              </section>
              {showCTAAfter ? <InlineCTA location={location} /> : null}
            </div>
          );
        }
        // FAQ accordion — map renders just above it
        const faqId = tocItems[tocCursor++]?.id ?? `faq-${i}`;
        return (
          <div key={i}>
            {mapSlot}
            <section className="bg-background px-6 py-20 lg:px-8">
              <div className="mx-auto max-w-4xl">
                <p className="text-xs font-semibold uppercase tracking-widest text-accent">FAQs</p>
                <h2
                  id={faqId}
                  className="mt-3 font-serif text-3xl leading-tight text-brand sm:text-4xl scroll-mt-24"
                >
                  {section.heading}
                </h2>
                <p className="mt-3 text-muted-foreground">
                  Quick answers to the questions we hear most often from {location} employers.
                </p>
                <Accordion type="single" collapsible className="mt-8">
                  {section.faqs?.map((f, k) => (
                    <AccordionItem key={k} value={`faq-${k}`} className="border-border">
                      <AccordionTrigger className="text-left font-serif text-lg text-brand hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="article-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{f.a}</ReactMarkdown>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </section>
          </div>
        );
      })}
    </article>
  );
}
